import { EditIcon, InfiniteTable } from '@/components/_common';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { InfiniteTableProps } from '@/components/_common/InfiniteTable';
import StatusTag from '@/components/_common/StatusTag';
import {
  AccountPayableFieldOption,
  AccountPayableSituationColor,
  InstallmentList,
  InstallmentSituation,
  InstallmentSituationType,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Space, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';

type Props = InfiniteTableProps<InstallmentList> & {
  installmentsByPurchase: InstallmentList[];
};

const InstallmentByPurchaseList: React.FC<Props> = ({
  isFetching,
  fetchNextPage,
  hasNextPage,
  installmentsByPurchase,
}) => {
  const intl = useIntl();

  const columns: ColumnsType<InstallmentList> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.code' }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.code,
      sorter: true,
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.companies',
      }),
      width: 180,
      key: AccountPayableFieldOption.company,
      sorter: true,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    {
      dataIndex: 'creditor',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
      width: 220,
      key: AccountPayableFieldOption.creditor,
      sorter: true,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.description',
      }),
      width: COLUMNS_WIDTH.action2,
      key: AccountPayableFieldOption.description,
      sorter: true,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    {
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentType',
      }),
      width: 200,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    {
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.dueDate,
      sorter: true,
    },
    {
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.situation',
      }),
      width: COLUMNS_WIDTH.price,
      render: (situation: InstallmentSituation) => (
        <StatusTag
          label={InstallmentSituationType[situation]}
          color={AccountPayableSituationColor[situation]}
        />
      ),
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue + (record.discountIncrease ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.value,
      sorter: true,
    },
    {
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.actions' }),
      width: 60,
      align: 'center',
      fixed: 'right',
      render: (row: InstallmentList) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () =>
                window.open(
                  `/app/financial/billToPay/releaseBillToPay/detail/accountPayableId/${row.accountPayableId}/installmentPayableId/${row.id}`,
                  '_blank',
                ),
            }}
          />
        </Space>
      ),
    },
  ];
  return (
    <InfiniteTable<InstallmentList>
      columns={columns}
      dataSource={installmentsByPurchase}
      loading={isFetching}
      rowKey={(row) => row.id}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      scroll={{ x: getTableScrollX(columns) }}
      title={() => (
        <Typography.Title level={4}>Parcelas Geradas</Typography.Title>
      )}
    />
  );
};

export default InstallmentByPurchaseList;
